import { Icon } from "@captions-web/icons";
import { t } from "i18next";
import { Dialog as RadixDialog } from "radix-ui";
import { forwardRef } from "react";

import { css, cva, cx, RecipeVariantProps } from "~/styled-system/css";

import { Button } from "./button";

const contentStyles = cva({
  // TODO: figure out better styles across variants for the dialogs

  base: {
    layerStyle: "blur-lg",

    outline: "none",
    borderRadius: "14px",
    color: "$gray.200",
  },

  variants: {
    variant: {
      plain: {},
    },
    align: {
      center: {
        margin: 16,
        placeSelf: "center",
      },
      right: {
        margin: 16,
        justifySelf: "flex-end",
      },
    },
  },

  defaultVariants: {
    align: "center",
  },
});

const overlayStyles = css({
  position: "fixed",
  inset: 0,
  zIndex: "$dialog",

  display: "grid",

  backgroundColor: "rgba(0, 0, 0, 0.5)",
});

type ContentProps = RadixDialog.DialogContentProps &
  RecipeVariantProps<typeof contentStyles> & {
    closeOnOutsideClick?: boolean;
    closeOnEscapeKey?: boolean;
  };

const Content = forwardRef<HTMLDivElement, ContentProps>(function Content(
  { variant, align, className, closeOnOutsideClick = true, closeOnEscapeKey = true, ...props },
  ref
) {
  const onOutsideClick = closeOnOutsideClick ? undefined : (e: Event) => e.preventDefault();
  const onEscapeKeyDown = closeOnEscapeKey ? undefined : (e: KeyboardEvent) => e.preventDefault();

  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className={overlayStyles}>
        <RadixDialog.Content
          {...props}
          onPointerDownOutside={onOutsideClick}
          onInteractOutside={onOutsideClick}
          onEscapeKeyDown={onEscapeKeyDown}
          className={cx(contentStyles({ variant, align }), className)}
          ref={ref}
        />
      </RadixDialog.Overlay>
    </RadixDialog.Portal>
  );
});

const simpleHeaderStyles = cva({
  base: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  variants: {
    size: {
      sm: { padding: "12px" },
      md: { padding: "24px 24px 16px 24px" },
    },
  },
});

const simpleContentStyles = cva({
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "0 24px 12px 24px",
  },

  variants: {
    size: {
      sm: { padding: "0 24px 12px 24px" },
      md: { padding: "0 24px 16px 24px" },
    },
    bleedContent: {
      true: { padding: "0" },
    },
  },
});

const simpleFooterStyles = cva({
  base: {
    display: "flex",
    justifyContent: "flex-end",
  },

  variants: {
    size: {
      sm: { padding: "0 12px 12px" },
      md: { padding: "0 24px 24px" },
    },
  },
});

interface SimpleContentProps extends Omit<RadixDialog.DialogContentProps, "title"> {
  title: React.ReactNode;
  cta?: React.ReactNode;
  size?: "sm" | "md";
  bleedContent?: boolean;

  closeOnOutsideClick?: boolean;
  closeOnEscapeKey?: boolean;
  hideCloseButton?: boolean;
}

const SimpleContent = forwardRef<
  HTMLDivElement,
  SimpleContentProps & RecipeVariantProps<typeof contentStyles>
>(function SimpleContent(
  {
    title,
    children,
    cta,
    className,
    variant,
    align,
    size = "md",
    bleedContent = false,
    closeOnOutsideClick = true,
    closeOnEscapeKey = true,
    hideCloseButton = false,
    ...props
  },
  ref
) {
  const onOutsideClick = closeOnOutsideClick ? undefined : (e: Event) => e.preventDefault();
  const onEscapeKeyDown = closeOnEscapeKey ? undefined : (e: KeyboardEvent) => e.preventDefault();

  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className={overlayStyles}>
        <RadixDialog.Content
          {...props}
          onPointerDownOutside={onOutsideClick}
          onInteractOutside={onOutsideClick}
          onEscapeKeyDown={onEscapeKeyDown}
          className={cx(contentStyles({ variant, align }), className)}
          ref={ref}
        >
          <header className={simpleHeaderStyles({ size })}>
            <RadixDialog.Title className={css({ textStyle: "body-1-subtle", color: "$gray.200" })}>
              {title}
            </RadixDialog.Title>

            <RadixDialog.Close
              asChild
              // keep title aligned when close button is hidden
              style={{
                visibility: hideCloseButton ? "hidden" : "visible",
              }}
            >
              <Button icon={<Icon name="cross-small" />} iconPosition="only" variant="blur" />
            </RadixDialog.Close>
          </header>
          <div className={simpleContentStyles({ size, bleedContent })}>{children}</div>
          {cta && <footer className={simpleFooterStyles({ size })}>{cta}</footer>}
        </RadixDialog.Content>
      </RadixDialog.Overlay>
    </RadixDialog.Portal>
  );
});

interface QuickActionContentProps extends ContentProps {
  title: string;
  cta: React.ReactNode;
}

const quickActionContentStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "430px",
  padding: "24px",
});

const quickActionHeaderStyles = css({});

const quickActionFooterStyles = css({
  display: "flex",
  justifyContent: "flex-end",
  gap: "8px",
});

const QuickActionContent = forwardRef<HTMLDivElement, QuickActionContentProps>(
  function QuickActionContent({ title, cta, children, className, ...props }, ref) {
    return (
      <Content {...props} ref={ref} className={cx(quickActionContentStyles, className)}>
        <header className={quickActionHeaderStyles}>
          <RadixDialog.Title className={css({ textStyle: "body-1-subtle", color: "$gray.200" })}>
            {title}
          </RadixDialog.Title>
        </header>
        <div className={simpleContentStyles({ size: "sm", bleedContent: true })}>{children}</div>
        {cta && (
          <footer className={quickActionFooterStyles}>
            <RadixDialog.Close asChild>
              <Button variant="secondary" size="md" overBlurredSurface>
                {t("common:cancel")}
              </Button>
            </RadixDialog.Close>
            {cta}
          </footer>
        )}
      </Content>
    );
  }
);

const { Portal: _, ...remainingRadixExports } = RadixDialog;

export const Dialog = {
  ...remainingRadixExports,
  Content,
  SimpleContent,
  QuickActionContent,
};
